/**
 * The native (non-custom-field) paths a metric aggregation or query can be
 * grouped by (`group_by`).
 *
 * A group key must take exactly one value per metric row, and
 * `device.device_id` is a column on the row itself. The service groups by no
 * other native field a metric request filters on. Among them,
 * `collection.collection_id` and tags hold several values, so a session in
 * three collections would multiply its own data point across three series;
 * `session.id` and `session.name` are unique per session, so every series
 * would have one member; `session.duration` and the session timestamps are
 * continuous, so every distinct value would be its own series.
 */
const METRIC_GROUP_BY_NATIVE_FIELDS: ReadonlySet<string> = new Set([
  "device.device_id",
]);

/**
 * The prefixes of the custom-field paths a metric request can be grouped by:
 * `<entity>.custom.<name>`, for the two entities a metric row has at most one
 * of.
 *
 * A session's custom fields key off its session id, and a device's off the
 * `device_id` already on the metric row, so each metric row joins at most one
 * custom-field row. A collection's do not: a session belongs to any number
 * of collections, so grouping by one of their custom fields would enter a
 * session's data point into several series at once, which is also why
 * `collection.collection_id` is not a native group-by field. The service
 * accepts a custom field of these prefixes only when it is a String, Enum or
 * Boolean field in the Ready state.
 */
const METRIC_GROUP_BY_CUSTOM_FIELD_PREFIXES = [
  "device.custom.",
  "session.custom.",
];

/**
 * Which kind of `group_by` path `field` is, in the singular spelling boards
 * and the Metrics dashboard write: `native` for a column of the metric row,
 * `custom` for a session's or a device's custom field, undefined for any
 * other path. The service still refuses a `custom` path whose field is not a
 * Ready String, Enum or Boolean field, or whose name is not a custom field
 * name; a caller that knows the org's custom fields checks the type itself,
 * and a board definition, parsed without them, leaves it to the service.
 */
export function metricGroupByFieldKind(
  field: string,
): "native" | "custom" | undefined {
  if (METRIC_GROUP_BY_NATIVE_FIELDS.has(field)) {
    return "native";
  }
  return METRIC_GROUP_BY_CUSTOM_FIELD_PREFIXES.some(
    (prefix) => field.startsWith(prefix) && field.length > prefix.length,
  )
    ? "custom"
    : undefined;
}
